{
    "introduction": {
        "title": "Hi, I'm Cephable.",
        "back": "Back to introduction",
        "description": "I'm your personal AI to help you control, create, and automate using the tools you already love — across all your connections. There are a few things you should know about me.",
        "action": "Let's get started!",
        "recording": {
            "title": "Your voice and video stay local.",
            "description": "I never record, stream, or send your audio or video to the cloud. All inputs are processed on-device — privately and securely."
        },
        "data": {
            "title": "Your data is yours.",
            "description": "I don't use your commands or activity to train models for others. Everything stays private — and your app controls move with you across connections."
        },
        "offline": {
            "title": "I work when other tools don't.",
            "description": "Intelligent Tasks like generating content, rewriting, translating, and dictation all run locally. No cloud required. That means I work even when you're offline — and your data doesn't leave your device."
        },
        "empower": {
            "title": "Built to empower, not exploit.",
            "description": "I am designed to enhance your control — not monitor it. No invasive tracking. No creepy data collection. Just input that works for you."
        }
    },
    "my-capabilities": {
        "title": "Perfect! Here's how I can help.",
        "back": "Back to capabilities",
        "actionTitle": "See Me In Action",
        "basedOnInterests": "Based on your interests",
        "description": "Here are the features and capabilities most relevant to your workflow — organized across the three things Cephable does.",
        "action": {
            "title": "See Me In Action",
            "description": "Explore some examples of how I can assist you in your daily tasks."
        },
        "cta": {
            "eyebrow": "Ready when you are",
            "title": "See it work in your apps"
        }
    },
    "upgrade": {
        "pill": "{{license}} Unlocked",
        "title": "You've been upgraded to {{license}}.",
        "subtitle": {
            "trial": "For the next {{days}} days, my AI features are unlocked — Create and Automate, both running on one on-device model. Turn them on whenever you're ready.",
            "org": "You've been added to {{organization}} organization which unlocks my AI features — Create and Automate, both running on one on-device model. Turn them on whenever you're ready.",
            "orgGeneric": "Your organization has unlocked my AI features — Create and Automate, both running on one on-device model. Turn them on whenever you're ready."
        },
        "badge": {
            "trial": "Trial ends in",
            "trialDays_one": "{{count}} day",
            "trialDays_other": "{{count}} days",
            "org": "Unlocked by",
            "orgGeneric": "Organization license"
        },
        "setup": {
            "eyebrow": "Set up your Pro features",
            "title": "Choose what to turn on",
            "description": "You can change any of this later in Settings. Everything runs on-device — your words and files stay with you."
        },
        "enable": {
            "title": "Enable AI features",
            "description": "One switch turns on both Create and Automate. They run on the shared on-device model — no separate setup for each.",
            "model": {
                "label": "On-device AI model",
                "tag": "Shared engine",
                "description": "Powers both Create and Automate. Larger models produce better results but use more resources — it downloads and runs entirely on this device."
            }
        },
        "create": {
            "title": "Create",
            "description": "Intelligent text generation & dictation. I'll draft emails, documents, code, and creative content — and turn your speech into formatted text in any app.",
            "locked": "Enable AI features above and Create is ready to use."
        },
        "automate": {
            "title": "Automate",
            "description": "AI Workflows. Describe a task in one sentence and I'll plan and carry out every step — reading, writing, and running commands on your behalf.",
            "locked": "Enable AI features above and Automate is ready to use."
        },
        "status": {
            "on": "On",
            "off": "Won't run"
        },
        "footer": "Everything runs on-device. Nothing you create or automate leaves your machine.",
        "cta": {
            "eyebrow": "You can enable AI anytime",
            "title": "Let's see it work in your apps",
            "action": "See Me In Action",
            "back": "Back"
        }
    },
    "see-me": {
        "title": "Want to see how I've helped others like you?",
        "back": "Back to use cases",
        "description": "Check out some real-world examples of how I've transformed workflows across different industries and roles."
    },
    "complete": {
        "title": "Ready to Start Using Me?",
        "description": "You've seen what I can do! Now let's get you set up and start transforming your workflow. I'll be here to help you every step of the way.",
        "action": "Complete Setup & Start Using Cephable"
    },
    "apps": {
        "eyebrow": "200+ Apps, Zero Setup",
        "title": "I Support the Tools You Already Love",
        "description": "With pre-configured support for 200+ applications — I work with the software you use every day. No setup required.",
        "note": "Don't see an app you use? No problem — I can generate controls or help you create custom workflows that work for you.",
        "andMore": "And More"
    },
    "actions": {
        "ctaTry": "Try it now",
        "ctaWatch": "Watch demo",
        "apps": "I am Compatible with:",
        "abilities": "My abilities used:"
    },
    "interests": {
        "title": "What can I help you with?",
        "back": "Back to interests",
        "description": "I can control apps on your desktop and connected devices. Let's personalize your experience - what areas interest you most? ",
        "note": "Select all that apply and I'll show you the most relevant features and examples for your workflow.",
        "action": "Continue with {{count}} interests",
        "empty": "Select at least one interest.",
        "filter": "Filter by Interests Type",
        "create_design": {
            "label": "Create & Design",
            "shortLabel": "Design",
            "description": "For artists, marketers, and content creators."
        },
        "analyze_report": {
            "label": "Analyze & Report",
            "shortLabel": "Analyze",
            "description": "For analysts, researchers, and business professionals."
        },
        "communicate_collaborate": {
            "label": "Communicate & Collaborate",
            "shortLabel": "Communicate",
            "description": "For sellers, meetings, and remote teams."
        },
        "build_develop": {
            "label": "Build & Develop",
            "shortLabel": "Develop",
            "description": "For developers, engineers, and technical professionals."
        },
        "game_play": {
            "label": "Game & Play",
            "shortLabel": "Gaming",
            "description": "For gamers, streamers, and media controls."
        },
        "learn_teach": {
            "label": "Learn & Teach",
            "shortLabel": "Learning",
            "description": "For educators, students, and instruction."
        },
        "manage_organize": {
            "label": "Manage & Organize",
            "shortLabel": "Organizing",
            "description": "For project managers, coordinators, and organizers."
        },
        "adapt_access": {
            "label": "Adapt & Access",
            "shortLabel": "Accessibility",
            "description": "For adaptive controls and personalized accessibility needs."
        },
        "everything": {
            "label": "A little bit of everything",
            "shortLabel": "Everything",
            "description": "For general computer users and multi-taskers."
        }
    },
    "control": {
        "title": "Control.",
        "titleAlt": "Control",
        "subtitle": "Your software. Your inputs. Your rules.",
        "description": "Voice commands, keyboard shortcuts, and quick action buttons — working across every app on your device. No mouse required. No scripting. No setup for 100's of apps you already use."
    },
    "create": {
        "title": "Create.",
        "titleAlt": "Create",
        "subtitle": "Your words. Your drafts. Your machine.",
        "description": "Draft, rewrite, reformat, and translate — right inside the app you’re working in. No cloud. No copy-paste into another tool. No drafts training someone else’s model."
    },
    "automate": {
        "title": "Automate.",
        "titleAlt": "Automate",
        "subtitle": "One sentence. Every step. Done.",
        "description": "Describe a job in plain language and Cephable carries it out. Across your apps, your files, even your command line. It runs while you do something else, and nothing leaves your machine."
    },
    "capabilities": {
        "title": "My Core AI Capabilities",
        "description": "I combine advanced AI with powerful input methods to give you seamless control over your apps and devices. Here are some of the things I can do.",
        "voice_controls": {
            "title": "Voice Controls",
            "description": "I understand natural language commands so you can control any app with your voice.",
            "longDescription": "I let you speak naturally and still get things done. Whether you want to open apps, navigate menus, or trigger workflows, I translate plain English into hands-free and reliable actions."
        },
        "smart_dictation": {
            "title": "Intelligent Dictation",
            "description": "I convert your speech to text with context awareness and formatting.",
            "longDescription": "I listen carefully and capture your words accurately. I add punctuation, fix capitalization, and apply context-aware formatting, so your writing looks polished without extra effort."
        },
        "ai_text_generation": {
            "title": "AI Text Generation",
            "description": "I can create emails, documents, code, and creative content on the fly.",
            "longDescription": "I help you draft, summarize, or expand on anything directly in your apps. From emails and notes to code snippets and documents, I make sure you never start from a blank page."
        },
        "head_face_controls": {
            "title": "Head & Face Controls",
            "description": "I let you trigger actions with your head and facial movements.",
            "longDescription": "I translate your natural head movements and expressions into precise input. You can send an email with a smile, turn a page with a nod, or click with a raise of the eyebrows."
        },
        "natural_language_voice_clicking": {
            "title": "Natural Language Voice Clicking",
            "description": "I let you say what you see to click buttons, links, and menus on screen.",
            "longDescription": "When you speak the name of something on your screen, I find it and interact with it for you. No memorized commands — just describe what you want and I’ll handle the click."
        },
        "keyboard_mouse_emulation": {
            "title": "Keyboard & Mouse Emulation",
            "description": "I emulate keys, clicks, and scrolling so you can control apps without hardware.",
            "longDescription": "I send keystrokes, mouse movements, clicks, and scroll events anywhere you need them. Combine me with voice or head controls to fully replace or enhance traditional input devices."
        },
        "profiles_personalization": {
            "title": "Personalization",
            "description": "I remember your preferences and adapt with saved app controls.",
            "longDescription": "I keep track of your favorite apps, commands, and preferences. Switch between app controls automatically or manually, and I’ll always show the right controls for what you’re doing."
        },
        "cross_device_connectivity": {
            "title": "Cross-Device Connectivity",
            "description": "I let you use your phone to start an automation remotely.",
            "longDescription": "I connect your mobile device to your desktop so you can launch commands, dictate, or navigate with ease. Perfect for presentations, remote sessions, or casual use."
        },
        "quick_action_buttons": {
            "title": "Quick Action Buttons",
            "description": "I give you app-aware buttons for fast actions.",
            "longDescription": "I surface context-sensitive buttons that adapt to your app. Whether it’s joining a meeting, sending a message, or creating a new email, I make complex tasks just one click away."
        },
        "background_creation": {
            "title": "Background Creation",
            "description": "I create and edit content in the background while you work.",
            "longDescription": "I create and edit content in the background while you focus on your work. When you need it, the content is ready for you in your file system without any interruption to your workflow or having to copy."
        },
        "ai_app_awareness": {
            "title": "AI App Awareness",
            "description": "I adjust my controls based on the app you’re using.",
            "longDescription": "I pay attention to what’s active on your screen so I can give you the right controls at the right time. No clutter, no guesswork—just what you need, when you need it. All on your device and without sending data to the cloud."
        },
        "ai_rewrite": {
            "title": "AI Rewrite",
            "description": "I can rewrite your text to improve tone, update formatting, or expand on details.",
            "longDescription": "I edit text right where you’re working. I can make it friendlier, more formal, shorter, longer, or more detailed — whatever you need to get your point across better."
        },
        "ai_summary": {
            "title": "AI Summary",
            "description": "Gather insights from long text and create short summaries or key takeaways."
        },
        "ai_translation": {
            "title": "AI Translation",
            "description": "I can translate between languages or even programming languages.",
            "longDescription": "I let you turn text into another spoken language or convert code into another programming language. I work in place, so you don’t have to leave the app you’re using."
        },
        "head_mouse": {
            "title": "Head Mouse",
            "description": "I let you move your mouse cursor with head movements.",
            "longDescription": "I smoothly translate your head motion into cursor motion. With features like glide and sensitivity tuning, I give you precise, comfortable, hands-free control."
        },
        "head_joystick": {
            "title": "Head Joystick",
            "description": "I let you use your head as a joystick for games or apps.",
            "longDescription": "I map your head movements onto joystick axes so you can play games or control creative software without hardware. You move naturally, and I translate it into analog input."
        },
        "mcp_agent": {
            "title": "MCP Agent",
            "description": "I can connect with external AI agents through the Model Context Protocol (MCP).",
            "longDescription": "I speak the Model Context Protocol, which lets me work alongside other AI agents and tools. That means I can coordinate tasks, exchange context, and extend my abilities with specialized external systems — all while keeping you in control."
        },
        "byo_ai_integration": {
            "title": "Bring Your Own AI",
            "description": "I let you enhance my existing AI with your own AI models or services to use with me.",
            "longDescription": "If you already have an AI service, model, or provider you trust, I can integrate it into my workflow. I give you the flexibility to choose the engine that powers generation, rewriting, or translation—whether it’s cloud, local, or custom. When you are offline or if the service is unavailable, I can fall back to my built-in capabilities."
        },
        "workflows": {
            "title": "Workflows",
            "description": "I help you chain together actions into repeatable workflows.",
            "longDescription": "I let you design and run multi-step processes that connect different inputs, commands, and outputs. With workflows, you can automate routine tasks or create complex interactions—triggered by your voice, or gestures — so I do the heavy lifting for you."
        },
        "showMore": "Show More",
        "showLess": "Show Less"
    },

    "listing": {
        "title": "Use Cases",
        "titleLong": "Explore How I Can Help You",
        "recommended": "Recommended For You",
        "exploreMore": "Explore More Use Cases",
        "description": "Discover everything I can do to enhance your workflow. From productivity boosts to creative assistance, find the perfect use cases tailored to your needs.",
        "all": "All",
        "search": "Search use cases, apps, or capabilities...",
        "noResults": "No results found. Try adjusting your search or filters.",
        "emptyAction": "View More Online"
    },
    "examples": {
        "universalAppControl": {
            "title": "Universal App Control",
            "description": "Control any application on your computer with voice commands and smart automation",
            "descriptionLong": "Take control of any application with powerful voice commands and intelligent automation. Our Universal App Control system learns how you work and adapts to your needs, making complex tasks simple and routine actions instant. From managing your productivity suite to controlling media and gaming applications, this feature works across your entire digital environment."
        },
        "editingVideos": {
            "title": "Voice Editing in CapCut — Navigate Timelines, Cut Clips, and Stay in the Story",
            "description": "Edit video using voice controls and dictation, so you can cut and arrange clips hands-free.",
            "descriptionLong": "Move through your timeline, trim and split clips, and shape your edit using just your voice. Stay focused on the story without clicking through menus or reaching for the mouse."
        },
        "figma": {
            "title": "Voice Commands for Figma and Canva — Control Layers, Switch Tools, and Type Copy Hands-Free",
            "description": "Edit images and designs using voice controls and smart dictation, for faster iterations.",
            "descriptionLong": "Bring your visual ideas to life with voice controlled image editing. Navigate faster, and iterate on designs without having to click through complex menus."
        },
        "documentSummary": {
            "title": "Private AI Desktop Agent That Reads Your Documents, Writes the Summary, and Sends It to Your Team",
            "description": "Use a private desktop agent to read documents, draft summaries, and share them with your team hands-free.",
            "descriptionLong": "Point your AI agent at a document and let it do the reading, summarizing, and sending for you. Keep everything running locally and private while you move through your work without lifting a finger."
        },
        "excelAnalysis": {
            "title": "Work Faster and Analyze in Excel with Private and Local AI Automation",
            "description": "Analyze spreadsheets and surface trends using private, local AI automation, hands-free.",
            "descriptionLong": "Research trends across a folder or document, ask questions against financial reports, and build toward a final summary. Let local AI handle the analysis while keeping your data private on your own machine."
        },
        "emailAutomation": {
            "title": "Automate Your Email — Local AI Workflows that Read Your Inbox, Draft Replies, and More!",
            "description": "Read your inbox, draft replies, and build templates using local AI workflows and dictation.",
            "descriptionLong": "Draft an email summarizing an article and call out the key technical details, or create reusable templates to move through your inbox faster. Let local AI and dictation handle the reading and writing while keeping everything private."
        },
        "powerpointBuilder": {
            "title": "AI Workflows That Turn Your Notes and Docs into a Finished PowerPoint | Ai Powerpoint Builder",
            "description": "Turn notes, docs, and spreadsheets into a finished PowerPoint using AI workflows, hands-free.",
            "descriptionLong": "Analyze an Excel document to find key losses, then let AI shape your notes and data into a polished presentation. Build slide decks faster without clicking through menus or starting from a blank page."
        },
        "socialDictation": {
            "title": "Voice to text for LinkedIn, Slack, and Discord | Post and Reply Without Typing",
            "description": "Post and reply on LinkedIn, Slack, and Discord using voice to text, no typing required.",
            "descriptionLong": "Draft posts, fire off replies, and stay active across LinkedIn, Slack, and Discord using just your voice. Keep the conversation going without reaching for the keyboard."
        },
        "developerTools": {
            "title": "Voice Controls + AI Automations for Developers | Write Comments, Docs, and Commit Messages",
            "description": "Write comments, docs, and commit messages using voice controls and AI automations, hands-free.",
            "descriptionLong": "Ask how to make the code in a project folder cleaner, then dictate comments, documentation, and commit messages without typing. Stay in flow while voice controls and AI automations handle the busywork."
        },

        "terminalAgent": {
            "title": "AI Desktop Agent | Run Terminal Builds and Tests in the Background While You Keep Coding",
            "description": "Run builds, tests, and debugging in the background with an AI desktop agent while you keep coding.",
            "descriptionLong": "Have your agent review a project folder, build it, confirm it compiles, and run test cases. If something breaks, it digs through git history to find the cause and suggests a fix, all while you stay focused on your work."
        },
        "gamingFPS": {
            "title": "Voice Controls for FPS and Valorant and more | Aim, Shoot, Reload, and Switch Weapons Hands-Free",
            "description": "Aim, shoot, reload, and switch weapons in FPS games like Valorant using voice controls, hands-free.",
            "descriptionLong": "Call out commands to shoot, reload, and swap weapons while you play FPS titles like Valorant. Add voice control to your setup and keep the action going without relying on every key and button."
        },
        "gamingRPG": {
            "title": "Customize RPG Game Controls for Minecraft | Adaptive Controls for Video Games",
            "description": "Customize and adapt game controls for Minecraft and other RPGs to fit how you play.",
            "descriptionLong": "Set up adaptive controls for Minecraft and other RPG-style games, mapping actions to suit your needs. Build a control scheme that fits how you play instead of forcing your hands to fit the game."
        },

        "educationTools": {
            "title": "Transform Classroom Notes and Articles into Creative Study Tools or Teaching Materials Using On Device AI",
            "description": "Turn notes and articles into study tools and teaching materials using on-device AI, hands-free.",
            "descriptionLong": "Take an article and create a Word document with reflection questions tailored to a student's grade level to support a creative writing assignment. Let on-device AI shape your source material into ready-to-use teaching resources while keeping everything private."
        },
        "meetingNotes": {
            "title": "AI Automations that Turn Meeting Notes into Smaller Files",
            "description": "Turn meeting notes into organized, actionable files using AI automations, hands-free.",
            "descriptionLong": "Read your meeting notes to surface what's left to do, who's responsible, and where the gaps are. Let AI outline these as separate files and organize them into a folder so you can review everything later."
        },
        "fileManagement": {
            "title": "On Device AI Desktop Agent | Organize Your Downloads, Find Files, and Clean Up Your PC",
            "description": "Organize downloads, find files, and clean up your PC with an on-device AI desktop agent, hands-free.",
            "descriptionLong": "Ask your agent to copy files from downloads into your product documentation directory, or have it find files and tidy up your PC. Keep your machine organized with on-device AI that keeps your data private."
        },
        "dictationWriting": {
            "title": "Hands-Free Typing and Document Creation — Voice Dictation, AI Generation, and Rewrite on Any App",
            "description": "Type, generate, and rewrite documents hands-free using voice dictation and AI in any app.",
            "descriptionLong": "Dictate text, generate new content, and rewrite what you've written across any app using just your voice. Create documents from start to finish without touching the keyboard."
        },

        "headMouseControl": {
            "title": "Hands-Free Computer Control with Head Mouse, Talk to Tap | Adaptive Computer Controls",
            "description": "Control your computer hands-free using head tracking and Talk to Tap adaptive controls.",
            "descriptionLong": "Move the cursor with head tracking and click using Talk to Tap to navigate your computer entirely hands-free. Set up adaptive controls that let you work the way that fits you best."
        },

        "documentTranslation": {
            "title": "AI Document Translation for Anyone Working Across Languages — Spanish, Arabic, Portuguese and More",
            "description": "Translate documents across languages like Spanish, Arabic, and Portuguese using AI, hands-free.",
            "descriptionLong": "Translate your documents into Spanish, Arabic, Portuguese, and more with on-device AI. Work confidently across languages without switching tools or copying text between apps."
        },

        "websiteRedesign": {
            "title": "Private AI Web Research + Automations | Browse, Analyze, and Create HTML",
            "description": "Research competitors and get suggestions for redesigning a website using AI, hands-free.",
            "descriptionLong": "Point AI at the SafeDrive Auto Insurance website, then have it research competitors' sites to spot the differences. Get concrete suggestions for modernizing an outdated site based on real market research."
        },
        "dataPrivacyResearch": {
            "title": "Browse, Analyze, and Summarize Without Your Data Leaving Your Device | Private AI Web Research ",
            "description": "Research the latest data privacy laws and turn findings into compliance follow-ups, hands-free.",
            "descriptionLong": "Research the most recent 2026 data privacy laws and how they differ from 2025, then surface what companies need to implement. Have AI draft a follow-up email with examples to help you meet compliance."
        }
    }
}
